import { del, get, post, put } from '../request';

// ---------- FreeSWITCH 全局 SIP 设定（/fs-settings）----------

export interface FsSetting {
  id: number;
  /** internal profile：坐席分机是否允许注册 */
  internal_auth_registrations: boolean;
  /** external profile：中继（网关）是否允许注册到 FS */
  external_auth_registrations: boolean;
  /** 坐席全局注册密码（GET 已脱敏 "******"；留空/掩码提交 = 不修改） */
  agent_sip_password?: string;
  /** 出局默认主叫号（空=网关/FS 默认，即坐席分机号；运营商要求报备号码时配置） */
  outbound_caller_id_number?: string;
  /** 出局默认主叫名（空=不设置） */
  outbound_caller_id_name?: string;
  /** 出局统一拨号前缀（仅数字，空=不拼接；只作用于实际拨号串，话单仍记所拨号码） */
  outbound_prefix?: string;
  updated_at?: string;
}

/** 最近一次生效动作回执（保存后到底做了什么） */
export interface ApplyResult {
  at: string;
  /** reloadxml / restart-internal / restart-external / rescan-external */
  actions: string[];
  ok: boolean;
  error?: string;
}

/** 已保存但尚未在 FS 生效的变更 */
export interface PendingChange {
  key: string;
  /** reloadxml | profile-restart | fs-restart */
  level: string;
  hint: string;
}

/** 软重启任务态（异步：POST 返回 running=true，结果靠 runtime 轮询） */
export interface RestartState {
  running: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  ok: boolean;
  error?: string;
  elapsed_ms: number;
  channels_before: number;
}

/** GET /fs-settings/runtime：FS 运行状态 + 重启任务态 */
export interface FsRuntime {
  connected: boolean;
  version?: string;
  uptime?: string;
  /** -1 = 未能解析运行时长 */
  uptime_sec: number;
  channels: number;
  calls: number;
  profiles: Record<string, string>;
  error?: string;
  restart?: RestartState;
}

/** GET /fs-settings 响应：注册开关 + 目录/片段同步状态 + 生效回执/待生效 */
export interface FsSettingsStatus {
  setting: FsSetting;
  dir_files: number;
  expected_files: number;
  stale_files: string[];
  settings_synced: boolean;
  gateway_name?: string;
  gateway_host?: string;
  gateway_count: number;
  vars_synced: boolean;
  /** outbound.xml（出局默认值）与 DB 是否一致 */
  outbound_synced: boolean;
  last_apply?: ApplyResult | null;
  pending: PendingChange[];
}

export function getFsSettings(): Promise<FsSettingsStatus> {
  return get('/fs-settings');
}

export function updateFsSettings(v: Partial<FsSetting>) {
  return put('/fs-settings', v);
}

/** POST /fs-settings/apply：手动全量重同步（坐席/中继目录 + 设置片段 + reloadxml） */
export function applyFsSettings(): Promise<FsSettingsStatus> {
  return post('/fs-settings/apply');
}

/** GET /fs-settings/runtime：FreeSWITCH 运行状态（版本/运行时长/通道/两个 profile 状态） */
export function getFsRuntime(): Promise<FsRuntime> {
  return get('/fs-settings/runtime');
}

/**
 * POST /fs-settings/restart-fs：软重启 FreeSWITCH（异步）。
 * 立即返回 running=true 的任务态；结果用 getFsRuntime() 轮询 restart 字段（后端探活最长 90s）。
 */
export function restartFreeSWITCH(): Promise<RestartState> {
  return post('/fs-settings/restart-fs', { confirm: true });
}

// ---------- 交换引擎（/fs-settings/engine，双核运行时切换）----------

/** GET/POST /fs-settings/engine 响应：当前引擎与能力位 */
export interface EngineInfo {
  /** 运行期实际引擎：freeswitch | zitch */
  engine: string;
  /** 系统设置里记录的引擎（与运行期一致） */
  configured: string;
  /** 当前实例是否支持运行时切换 */
  switchable: boolean;
  connected: boolean;
  /** 在途通道数（切换前判断） */
  channels: number;
  targets: string[];
}

/** GET /fs-settings/engine：当前交换引擎（只读） */
export function getTelephonyEngine(): Promise<EngineInfo> {
  return get('/fs-settings/engine');
}

/** POST /fs-settings/engine：运行时切换交换引擎（断开并重连 ESL，坐席软电话需重新注册） */
export function switchTelephonyEngine(
  engine: 'freeswitch' | 'zitch',
  force = false
): Promise<EngineInfo> {
  return post('/fs-settings/engine', { engine, force });
}

// ---------- 中继注册账号（/trunk-accounts，如 HX4G FXO 8000/8001）----------

export interface TrunkAccount {
  id: number;
  extension: string;
  /** 列表已脱敏（"******"） */
  password?: string;
  enabled: boolean;
  note?: string;
  created_at?: string;
  updated_at?: string;
}

export function listTrunkAccounts(): Promise<TrunkAccount[]> {
  return get('/trunk-accounts');
}

/** 编辑时 password 留空或 "******" = 不修改 */
export function createTrunkAccount(v: {
  extension: string;
  password: string;
  enabled?: boolean;
  note?: string;
}) {
  return post('/trunk-accounts', v);
}

export function updateTrunkAccount(
  id: number,
  v: { extension?: string; password?: string; enabled?: boolean; note?: string }
) {
  return put(`/trunk-accounts/${id}`, v);
}

export function deleteTrunkAccount(id: number) {
  return del(`/trunk-accounts/${id}`);
}
